import { useState } from "react"
import { Alert, Button, Group, Stack, Text } from "@mantine/core"
import { AuthError } from "@azure/msal-browser"
import { IconBrandWindowsFilled } from "@tabler/icons-react"
import { loginRequest, pca } from "@features/auth/config/msalConfig"
import { loginMicrosoft } from "@features/auth/hooks/auth.queries"
import { LocalStorageSession } from "@shared/utils/local-storage-session"

class ReLoginError extends Error {}

interface SessionExpiredContentProps {
  email: string
  tenantId: string
  onDone: (token: string | null) => void
}

export function SessionExpiredContent({ email, tenantId, onDone }: SessionExpiredContentProps) {
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = async () => {
    setIsLoggingIn(true)
    setError(null)
    try {
      const response = await pca.loginPopup({ ...loginRequest, loginHint: email, redirectUri: `${globalThis.location.origin}/auth.html` })
      const account = response.account
      if (!account || !response.idToken) throw new ReLoginError("No se pudo obtener tu cuenta de Microsoft. Inténtalo nuevamente.")
      if (account.username.toLowerCase() !== email.toLowerCase()) throw new ReLoginError(`Ingresa con la misma cuenta con la que estabas trabajando (${email}).`)

      const loginResponse = await loginMicrosoft({ email: account.username, useMicrosoft: true, microsoftToken: response.idToken, tenantId })
      LocalStorageSession.saveAuthorization(loginResponse.user, loginResponse.accessToken)
      onDone(loginResponse.accessToken)
    } catch (e) {
      if (e instanceof ReLoginError) setError(e.message)
      else if (e instanceof AuthError && e.errorCode === "user_cancelled") setError("Cerraste la ventana de Microsoft antes de terminar.")
      else setError("No se pudo iniciar sesión. Inténtalo nuevamente.")
    } finally {
      setIsLoggingIn(false)
    }
  }

  return (
    <Stack gap="md">
      <Text size="sm">Tu sesión expiró. Vuelve a ingresar con tu cuenta de Microsoft para continuar: no perderás lo que estabas haciendo en esta pantalla.</Text>
      {error && (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      )}
      <Group justify="space-between">
        <Button variant="subtle" color="gray" disabled={isLoggingIn} onClick={() => onDone(null)}>
          Cerrar sesión
        </Button>
        <Button leftSection={<IconBrandWindowsFilled size={16} />} loading={isLoggingIn} onClick={handleLogin}>
          Ingresar con Microsoft
        </Button>
      </Group>
    </Stack>
  )
}
